// (C) Copyright 2015 Moodle Pty Ltd.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Injectable } from '@angular/core';
import { CoreSite } from '@classes/sites/site';
import { CoreModals } from '@services/modals';
import { CoreSites } from '@services/sites';
import { CoreUtils } from '@services/utils/utils';
import { CoreWS, CoreWSAjaxPreSets } from '@services/ws';
import { makeSingleton } from '@singletons';
import { CoreUrl } from '@singletons/url';
import { Workplace, WorkplaceTenantLoginInfo } from '@workplace/services/workplace';
import { WP_TENANTS_CHANGED_EVENT } from '../constants';
import { CoreEvents } from '@singletons/events';

/**
 * Service that provides helper functions to workplace.
 */
@Injectable({ providedIn: 'root' })
export class WorkplaceTenantsHelperService {

    // Cache to test tenantUrl param on ajax calls.
    protected tenantUrlEnabled: Record<string, boolean> = {};

    /**
     * Initialize.
     */
    async initialize(): Promise<void> {
        WorkplaceTenantsHelper.removeTenantInfo();

        CoreEvents.on(CoreEvents.LOGIN, async (data): Promise<void> => {
            await WorkplaceTenantsHelper.initTenantOnLogin(data.siteId);
        });

        CoreEvents.on(CoreEvents.LOGOUT, (): void => {
            WorkplaceTenantsHelper.removeTenantInfo();
        });

        // Remove site saved tenant.
        CoreEvents.on(CoreEvents.SITE_DELETED, (site) => {
            if (site?.id) {
                WorkplaceTenantsHelper.removeTenantInfo(site.id);
            }
        });
    }

    /**
     * Check and saves the tenant id from the URL.
     *
     * @param url URL to check.
     * @param siteUrl Site URL. To perform the call.
     */
    async saveTenantInfoFromUrl(url: string, siteUrl?: string): Promise<void> {
        const tenantFound = await this.getTenantLoginInfoFromUrl(url, siteUrl);

        if (!tenantFound) {
            this.removeTenantInfo();

            return;
        }

        const tenantInfo = this.getTenantInfoFromUrl(tenantFound.url);

        this.saveTenantInfo(tenantInfo);
    }

    /**
     * Retrieve tenant login info to match a url.
     *
     * @param url URL to check.
     * @param siteUrl Site URL. To perform the call.
     * @returns Tenant Login info (url, logo and title).
     */
    async getTenantLoginInfoFromUrl(url: string, siteUrl?: string): Promise<WorkplaceTenantLoginInfo | undefined> {
        url = CoreUrl.formatURL(url);

        const tenants = await Workplace.getTenantsInfo(siteUrl ?? url);
        if (!tenants.length) {
            return;
        }

        let tenantFound: WorkplaceTenantLoginInfo | undefined = tenants.find((tenant) => tenant.url === url);

        const params = CoreUrl.extractUrlParams(url);
        if (!tenantFound && params.tenantid) {
            const tenantId = parseInt(params.tenantid, 10);
            tenantFound = tenants.find((tenant) => tenant.id === tenantId);
        }

        if (!tenantFound && params.tenant) {
            tenantFound = tenants.find((tenant) => tenant.idnumber === params.tenant);
        }

        if (!tenantFound && siteUrl) {
            tenantFound = tenants.find((tenant) => tenant.url === siteUrl);
        }

        return tenantFound ?? tenants[0];  // Fallback to the first tenant.
    }

    /**
     * Get tenant info from URL.
     *
     * @param tenantUrl Tenant URL.
     * @returns Tenant Info.
     */
    protected getTenantInfoFromUrl(tenantUrl: string): WorkplaceTenantInfo | undefined {
        if (!tenantUrl) {
            return;
        }

        const tenantInfo: WorkplaceTenantInfo = { url: tenantUrl };

        const params = CoreUrl.extractUrlParams(tenantUrl);
        if (params.tenantid) {
            tenantInfo.id = params.tenantid;
        }

        return tenantInfo;
    }

    /**
     * Save tenant info and returns if tenantId is avaiable.
     *
     * @param tenantInfo Tenant Info.
     * @param siteId Site Id. If not specified, temp name will be used.
     */
    protected saveTenantInfo(tenantInfo?: WorkplaceTenantInfo, siteId?: string): void {
        siteId = siteId || 'temp';

        if (tenantInfo?.id) {
            localStorage.setItem(`tenantId-${siteId}`, tenantInfo.id);
        } else {
            localStorage.removeItem(`tenantId-${siteId}`);
        }

        if (tenantInfo?.url) {
            localStorage.setItem(`tenantUrl-${siteId}`, tenantInfo.url);
        } else {
            localStorage.removeItem(`tenantUrl-${siteId}`);
        }

    }

    /**
     * Return saved tenant info.
     *
     * @param siteId Site Id. If not specified, temp name will be used.
     * @returns Tenant info.
     */
    protected async getTenantInfo(siteId?: string): Promise<WorkplaceTenantInfo | undefined> {
        siteId = siteId || 'temp';

        let url = localStorage.getItem(`tenantUrl-${siteId}`);
        let site: CoreSite | undefined = undefined;

        if (siteId !== 'temp') {
            // Guess tenant url.
            site = await CoreSites.getSite(siteId);
            url = site.getURL();
        }
        if (!url) {

            return;
        }

        let id = localStorage.getItem(`tenantId-${siteId}`) ?? undefined;
        if (siteId !== 'temp') {
            site = site ?? await CoreSites.getSite(siteId);

            const configTenant = await CoreUtils.ignoreErrors(site.getConfig('wp_tool_tenant_config_id'));
            if (configTenant) {
                id = configTenant;
            }
        }

        if (site) {
            this.saveTenantInfo({ id, url }, siteId);
        }

        return { id, url };
    }

    /**
     * Resets site tenant id.
     *
     * @param siteId Site Id. If not specified, temp name will be used.
     */
    removeTenantInfo(siteId?: string): void {
        siteId = siteId || 'temp';

        localStorage.removeItem(`tenantUrl-${siteId}`);
        localStorage.removeItem(`tenantId-${siteId}`);
    }

    /**
     * Retrieves tenant for a siteId or temporary if not set.
     *
     * @returns Tenant URL or undefined.
     */
    async getTenantUrl(): Promise<string | undefined> {
        const siteId = CoreSites.getCurrentSiteId();
        const tenantInfo = await this.getTenantInfo(siteId);

        return tenantInfo?.url ?? undefined;
    }

    /**
     * Retrieves tenant for a siteId.
     *
     * @returns Tenant Id or undefined.
     */
    async getTenantId(): Promise<number | undefined> {
        const siteId = CoreSites.getCurrentSiteId();
        const tenantInfo = await this.getTenantInfo(siteId);

       return tenantInfo?.id ? parseInt(tenantInfo.id, 10) : undefined;
    }

    /**
     * Get stored tenant url.
     *
     * @param siteUrl Site url.
     * @returns Checked tenant url or undefined.
     */
    async checkTenantUrl(siteUrl: string): Promise<string | undefined> {
        const tenanturl = await this.getTenantUrl();
        if (!tenanturl) {
            return undefined;
        }

        if (this.tenantUrlEnabled[siteUrl] !== undefined) {
            return this.tenantUrlEnabled[siteUrl] ? tenanturl : undefined;
        }

        // This checks, the ws is capable of reading the tenanturl param.
        const preSets: CoreWSAjaxPreSets = {
            siteUrl: siteUrl,
            noLogin: true,
            useGet: true,
        };
        this.tenantUrlEnabled[siteUrl] = await CoreUtils.promiseWorks(CoreWS.callAjax(
            'tool_mobile_get_public_config',
            { tenanturl },
            preSets,
        ));

        if (!this.tenantUrlEnabled[siteUrl]) {
            // tenanturl is not supported, do not use it.
            return undefined;
        }

        return tenanturl;
    }

    /**
     * Saves temp tenant for the current siteId.
     *
     * @param siteId Site Id. Current siteId if undefined.
     */
    async initTenantOnLogin(siteId?: string): Promise<void> {
        const site = await CoreSites.getSite(siteId);
        if (!site) {
            return;
        }

        const tempTenant = await this.getTenantInfo();
        const siteTenant = await this.getTenantInfo(site.getId());

        if (!tempTenant || siteTenant) {
            return;
        }

        tempTenant.url = site.getURL(); // Update the url.

        this.saveTenantInfo(tempTenant, site.getId());
        this.removeTenantInfo();
        await site.loadTenantUrls();
    }

    /**
     * Open change tenant selector.
     *
     * @param tenants List of tenants
     * @returns Selected tenant.
     */
    async changeTenant(tenants: WorkplaceTenantLoginInfo[]): Promise<WorkplaceTenantLoginInfo | undefined> {
        const { WorkplaceTenantsSelectorComponent } =
            await import('@workplace/features/tenants/components/tenantsselector/tenantsselector');

        // Create the navigation modal.
        return await CoreModals.openModal<WorkplaceTenantLoginInfo>({
            component: WorkplaceTenantsSelectorComponent,
            componentProps: {
                tenants,
            },
        });
    }

}

export const WorkplaceTenantsHelper = makeSingleton(WorkplaceTenantsHelperService);

export type WorkplaceTenantInfo = {
    id?: string;
    url: string;
};

declare module '@singletons/events' {

    /**
     * Augment CoreEventsData interface with events specific to this service.
     *
     * @see https://www.typescriptlang.org/docs/handbook/declaration-merging.html#module-augmentation
     */
    export interface CoreEventsData {
        [WP_TENANTS_CHANGED_EVENT]: WorkplaceTenantLoginInfo;
    }

}
